'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Text, Container, Icon, useResizeObserver } from '@weareconceptstudio/core';

//* Styles
import SliderReviewsStyle from './style';

//* Components
import StyledQuotedText from '@/components/StyledQuotedText';
import Rating from '@/components/Rating';
import Editor from '@/components/Editor';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const SliderReviews = ({ pageData }) => {
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	//! Ref
	const swiperRef = useRef();
	const scrollTriggerRef = useRef();
	const styleRef = useRef();

	const resize = useResizeObserver(document.querySelector('body'));

	//! Animation
	useEffect(() => {
		if (pageData.length) {
			const itemSlider = swiperRef.current.children[0].children;
			const firstSeeChildren = [...itemSlider].slice(0, 3);

			if (firstSeeChildren.length == 3 && firstSeeChildren[0]?.classList?.contains('swiper-slide-active') && !firstSeeChildren[0].style.rotate) {
				const itemSlider = swiperRef.current?.children[0]?.children;
				if (itemSlider) {
					const firstSeeChildren = [...itemSlider].slice(0, 3);
					gsap.set(firstSeeChildren[0], { x: '80%', rotate: -20, y: '10%' });
					gsap.set(firstSeeChildren[1], { x: '0%', rotate: -10, y: '0' });
					gsap.set(firstSeeChildren[2], { x: '-80%', rotate: 20, y: '20%' });
				}
			}

			scrollTriggerRef.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top bottom',
				// markers: true,
				trigger: swiperRef.current,

				onEnter: () => {
					if (firstSeeChildren.length == 3 && firstSeeChildren[0]?.classList?.contains('swiper-slide-active')) {
						gsap.to(firstSeeChildren[0], { x: 0, rotate: 0, y: 0, duration: 0.9, ease: 'power2.out' });
						gsap.to(firstSeeChildren[1], { x: 0, rotate: 0, y: 0, duration: 0.9, ease: 'power2.out' });
						gsap.to(firstSeeChildren[2], { x: 0, rotate: 0, y: 0, duration: 0.9, ease: 'power2.out' });
					}
				},
				onLeaveBack: () => {
					if (firstSeeChildren.length == 3 && firstSeeChildren[0]?.classList?.contains('swiper-slide-active')) {
						gsap.killTweensOf([...firstSeeChildren]);
						gsap.set(firstSeeChildren[0], { x: '80%', rotate: -20, y: '10%' });
						gsap.set(firstSeeChildren[1], { x: '0%', rotate: -10, y: '0' });
						gsap.set(firstSeeChildren[2], { x: '-80%', rotate: 20, y: '20%' });
					}
				},
			});
		}

		return () => {
			if (scrollTriggerRef.current) scrollTriggerRef.current.kill();
		};
	}, [pageData, resize]);

	const itemSlide = useMemo(() => {
		return pageData.map((item) => {
			return (
				<SwiperSlide key={item.id}>
					<div className='wrapper-review-slider'>
						<Rating
							showText={false}
							className={'color-reverse'}
							rate_count={item.rating}
						/>
						<div className='wrapper-texts'>
							<Text className={'p1 font-larken-regular'}>{item.title}</Text>
							<Editor
								data={item.text}
								className={'text-review'}
							/>
						</div>
						<div className='wrapper-title'>
							<Text className={'p2 font-larken-regular'}>{item.author}</Text>
						</div>
					</div>
				</SwiperSlide>
			);
		});
	}, [pageData]);

	return pageData?.length > 0 ? (
		<SliderReviewsStyle ref={styleRef}>
			<Container isSection>
				<div className='top-block'>
					<div>
						<TitleAnim
							trigger={'bottom'}
							load={true}
							animRefTrigger={styleRef}
						>
							<StyledQuotedText text={'peoples" ""opinion"'} />
						</TitleAnim>
					</div>

					<div className='slider-icons-blok '>
						<div className={`slider-icon right-icon-wrap swiper-button-prev-review ${!isBeginning ? 'active' : 'inactive'}`}>
							<div className='wrapper-real-icon'>
								<Icon name={`arrow-right`} />
							</div>
							<div className='hover-icon-wrap right'>
								<Icon name={`arrow-right`} />
							</div>
						</div>

						<div className={`slider-icon left-icon-wrap swiper-button-next-review ${!isEnd ? 'active' : 'inactive'}`}>
							<div className='wrapper-real-icon left'>
								<Icon name={`arrow-left`} />
							</div>
							<div className='hover-icon-wrap'>
								<Icon name={`arrow-left`} />
							</div>
						</div>
					</div>
				</div>
				<div className={'slider-block'}>
					<div className='empty left-empty' />
					<Swiper
						ref={swiperRef}
						slidesPerView={'auto'}
						modules={[Navigation]}
						navigation={{
							prevEl: '.swiper-button-prev-review',
							nextEl: '.swiper-button-next-review',
						}}
						onSwiper={(swiper) => {
							setIsBeginning(swiper.isBeginning);
							setIsEnd(swiper.isEnd);
						}}
						onSlideChange={(swiper) => {
							setIsBeginning(swiper.isBeginning);
							setIsEnd(swiper.isEnd);
						}}
					>
						{itemSlide}
					</Swiper>
					<div className='empty right-empty' />
				</div>
			</Container>
		</SliderReviewsStyle>
	) : null;
};

export default SliderReviews;
